import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  ProgressBar,
  Select,
  Spinner,
  Switch,
  Tab,
  TabList,
  Textarea,
} from "@fluentui/react-components";
import {
  Add20Regular,
  BranchFork20Regular,
  Building20Regular,
  Copy20Regular,
  Delete20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Eye20Regular,
  Globe20Regular,
  Play20Regular,
  Sparkle20Regular,
  Wrench20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import x from "./Taskdesk.module.css";
import { ApiError, TASKS_API, api, crud, iso, msgOf, utc } from "./Api/Taskapi";
import { Chip, FormDialog, Head, Page, Tile, Wait, asList, fmtDate, fmtStamp, nice, useAct, useLoad } from "./Taskpages";
import type { Notify, PageCtx } from "./Taskpages";

/* ------------------------------------------------------------------ *
 *  Service desk extras, workflows and approvals, the assistant and the
 *  industry packs. Every control calls the Task Manager API; nothing
 *  here is sample data.
 * ------------------------------------------------------------------ */

type Row = Record<string, unknown> & { id: number };
const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const opts = (list: string[]) => list.map((v) => ({ value: v, label: nice(v) }));
const userName = (ctx: PageCtx, id: unknown) => (id === null || id === undefined ? "—" : (ctx.people.find((p) => p.id === Number(id))?.name ?? `User ${id}`));
const pretty = (json: unknown) => {
  try {
    return JSON.stringify(typeof json === "string" ? JSON.parse(json) : json, null, 2);
  } catch {
    return str(json);
  }
};
const parseJson = (s: unknown): unknown => {
  try {
    return typeof s === "string" && s.trim() ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};
const copy = (text: string, notify: Notify) => {
  void navigator.clipboard
    .writeText(text)
    .then(() => notify("success", "Copied."))
    .catch(() => notify("info", "Select the text and copy it."));
};
const localInput = (v: unknown) => {
  if (!v) return "";
  const d = utc(String(v));
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/* ================================================================== *
 *  A small create / edit / delete table for any entity
 * ================================================================== */
type FieldKind = "text" | "textarea" | "number" | "date" | "datetime" | "select" | "bool" | "user" | "project" | "json";
interface FieldDef {
  key: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  options?: { value: string; label: string }[];
  hint?: string;
  initial?: string | boolean;
  /** only shown when creating */
  createOnly?: boolean;
}
interface ColDef {
  key: string;
  label: string;
  render?: (row: Row) => React.ReactNode;
}
type FormState = Record<string, string | boolean>;

const emptyState = (fields: FieldDef[]): FormState => {
  const s: FormState = {};
  for (const f of fields) s[f.key] = f.initial ?? (f.kind === "bool" ? false : "");
  return s;
};
const stateFromRow = (fields: FieldDef[], row: Row): FormState => {
  const s: FormState = {};
  for (const f of fields) {
    const v = row[f.key];
    if (f.kind === "bool") s[f.key] = v === true;
    else if (f.kind === "datetime") s[f.key] = localInput(v);
    else if (f.kind === "date") s[f.key] = v ? str(v).slice(0, 10) : "";
    else if (f.kind === "json") s[f.key] = v ? pretty(v) : "";
    else s[f.key] = str(v);
  }
  return s;
};
/** form state -> request body; returns an error text when something is not valid */
const bodyFromState = (fields: FieldDef[], s: FormState, editing: boolean): { body: Record<string, unknown> } | { error: string } => {
  const body: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.createOnly && editing) continue;
    const raw = s[f.key];
    if (f.kind === "bool") {
      body[f.key] = raw === true;
      continue;
    }
    const t = typeof raw === "string" ? raw.trim() : "";
    if (!t) {
      if (f.required) return { error: `${f.label} is required.` };
      body[f.key] = null;
      continue;
    }
    if (f.kind === "number" || f.kind === "user" || f.kind === "project") {
      const n = Number(t);
      if (Number.isNaN(n)) return { error: `${f.label} must be a number.` };
      body[f.key] = n;
    } else if (f.kind === "datetime") body[f.key] = new Date(t).toISOString();
    else if (f.kind === "json") {
      try {
        JSON.parse(t);
      } catch {
        return { error: `${f.label} is not valid JSON.` };
      }
      body[f.key] = t;
    } else body[f.key] = t;
  }
  return { body };
};

const EntityDialog: React.FC<{
  open: boolean;
  title: string;
  action: string;
  fields: FieldDef[];
  state: FormState;
  setState: (s: FormState) => void;
  editing: boolean;
  busy: boolean;
  ctx: PageCtx;
  onClose: () => void;
  onSubmit: () => void;
}> = ({ open, title, action, fields, state, setState, editing, busy, ctx, onClose, onSubmit }) => (
  <FormDialog open={open} title={title} action={action} busy={busy} onClose={onClose} onSubmit={onSubmit}>
    {fields
      .filter((f) => !(f.createOnly && editing))
      .map((f) => {
        const val = state[f.key];
        const set = (v: string | boolean) => setState({ ...state, [f.key]: v });
        const common = { label: f.label, required: !!f.required, hint: f.hint ?? null };
        if (f.kind === "bool") return <Checkbox key={f.key} label={f.label} checked={val === true} onChange={(_, d) => set(d.checked === true)} />;
        if (f.kind === "textarea" || f.kind === "json")
          return (
            <Field key={f.key} {...common}>
              <Textarea className={f.kind === "json" ? x.mono : undefined} value={str(val)} rows={f.kind === "json" ? 6 : 3} resize="vertical" onChange={(_, d) => set(d.value)} />
            </Field>
          );
        if (f.kind === "select" || f.kind === "user" || f.kind === "project") {
          const list =
            f.kind === "user"
              ? ctx.people.map((p) => ({ value: String(p.id), label: p.name }))
              : f.kind === "project"
                ? ctx.projects.map((p) => ({ value: String(p.id), label: p.name }))
                : (f.options ?? []);
          return (
            <Field key={f.key} {...common}>
              <Select value={str(val)} onChange={(_, d) => set(d.value)}>
                <option value="">{f.required ? "Choose…" : "None"}</option>
                {list.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          );
        }
        const type = f.kind === "number" ? "number" : f.kind === "date" ? "date" : f.kind === "datetime" ? "datetime-local" : "text";
        return (
          <Field key={f.key} {...common}>
            <Input type={type} value={str(val)} onChange={(_, d) => set(d.value)} />
          </Field>
        );
      })}
  </FormDialog>
);

const MiniCrud: React.FC<{
  ctx: PageCtx;
  route: string;
  noun: string;
  fields: FieldDef[];
  cols: ColDef[];
  query?: Record<string, string | number | boolean | undefined>;
  extraCreate?: Record<string, unknown>;
  sort?: string;
  rowActions?: (row: Row, reload: () => void) => React.ReactNode;
  onChanged?: () => void;
  reloadKey?: number;
  canCreate?: boolean;
}> = ({ ctx, route, noun, fields, cols, query, extraCreate, sort = "-id", rowActions, onChanged, reloadKey = 0, canCreate = true }) => {
  const act = useAct(ctx.notify);
  const list = useLoad(
    async () => (await crud.list<Row>(route, { workspaceId: ctx.workspaceId, sort, ...(query ?? {}) })).items,
    [route, ctx.workspaceId, sort, JSON.stringify(query ?? {}), reloadKey],
  );
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<Row | null>(null);
  const [state, setState] = useState<FormState>(() => emptyState(fields));
  const [busy, setBusy] = useState(false);
  const rows = list.data ?? [];
  const done = () => {
    list.reload();
    onChanged?.();
  };
  const startNew = () => {
    setEditRow(null);
    setState(emptyState(fields));
    setOpen(true);
  };
  const startEdit = (r: Row) => {
    setEditRow(r);
    setState(stateFromRow(fields, r));
    setOpen(true);
  };
  const submit = async () => {
    const r = bodyFromState(fields, state, editRow !== null);
    if ("error" in r) {
      ctx.notify("error", r.error);
      return;
    }
    setBusy(true);
    const ok = await act(
      () => (editRow ? crud.update(route, editRow.id, r.body) : crud.create(route, { workspaceId: ctx.workspaceId, ...(extraCreate ?? {}), ...r.body })),
      editRow ? "Saved." : `${nice(noun)} added.`,
    );
    setBusy(false);
    if (ok) {
      setOpen(false);
      done();
    }
  };

  return (
    <div className={x.section}>
      {canCreate && (
        <div className={x.sectionBar}>
          <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={startNew}>
            New {noun}
          </Button>
        </div>
      )}
      <Wait loading={list.loading && !list.data} error={list.error} empty={!list.loading && rows.length === 0 && `No ${noun}s yet`} onRetry={list.reload} />
      {rows.length > 0 && (
        <div className={x.scroll}>
          <table className={x.tbl}>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  {cols.map((c) => (
                    <td key={c.key}>{c.render ? c.render(r) : str(r[c.key]) || "—"}</td>
                  ))}
                  <td className={x.rowActions}>
                    {rowActions?.(r, done)}
                    <Button appearance="subtle" size="small" icon={<Edit20Regular />} aria-label={`Edit ${noun}`} onClick={() => startEdit(r)} />
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Delete20Regular />}
                      aria-label={`Delete ${noun}`}
                      onClick={async () => {
                        if (await act(() => crud.remove(route, r.id), `${nice(noun)} deleted.`)) done();
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EntityDialog
        open={open}
        title={editRow ? `Edit ${noun}` : `New ${noun}`}
        action={editRow ? "Save" : "Add"}
        fields={fields}
        state={state}
        setState={setState}
        editing={editRow !== null}
        busy={busy}
        ctx={ctx}
        onClose={() => setOpen(false)}
        onSubmit={() => void submit()}
      />
    </div>
  );
};

const yesNo = (v: unknown) => <Chip text={v === true ? "Active" : "Off"} />;

/** tasks of the workspace to pick from (assistant, workflows, rules) */
const useTaskOptions = (ctx: PageCtx) => {
  const l = useLoad(async () => (await crud.list<{ id: number; title: string }>("tasks/tsk/task", { workspaceId: ctx.workspaceId, isCompleted: false, sort: "-id", pageSize: 200 })).items, [ctx.workspaceId]);
  return l.data ?? [];
};
const TaskSelect: React.FC<{ tasks: { id: number; title: string }[]; value: string; onChange: (v: string) => void; label?: string; optional?: boolean }> = ({ tasks, value, onChange, label = "Task", optional }) => (
  <Field label={label}>
    <Select value={value} onChange={(_, d) => onChange(d.value)}>
      <option value="">{optional ? "No task" : "Choose a task"}</option>
      {tasks.map((t) => (
        <option key={t.id} value={String(t.id)}>
          #{t.id} · {t.title}
        </option>
      ))}
    </Select>
  </Field>
);

/* ================================================================== *
 *  Ticket SLA + suggested articles (mount it in a ticket row)
 * ================================================================== */
interface SlaPart {
  dueAt?: string | null;
  doneAt?: string | null;
  minutesLeft?: number | null;
  breached: boolean;
}
interface SlaInfo {
  number: number;
  priority: string;
  status: string;
  escalationLevel: number;
  response: SlaPart;
  resolution: SlaPart;
  breachedAt?: string | null;
}
interface Article {
  id: number;
  title: string;
  snippet?: string | null;
  isPublished: boolean;
  score: number;
}
const left = (m?: number | null) => {
  if (m === null || m === undefined) return "";
  const a = Math.abs(m);
  const t = a >= 1440 ? `${Math.floor(a / 1440)}d ${Math.floor((a % 1440) / 60)}h` : a >= 60 ? `${Math.floor(a / 60)}h ${a % 60}m` : `${a}m`;
  return m < 0 ? `${t} overdue` : `${t} left`;
};

const SlaLine: React.FC<{ label: string; p: SlaPart }> = ({ label, p }) => (
  <div className={x.slaLine}>
    <span className={x.slaLabel}>{label}</span>
    <span>{p.dueAt ? fmtStamp(p.dueAt) : "No target"}</span>
    {p.doneAt ? <Chip text={p.breached ? "Late" : "Done"} /> : p.dueAt ? <Chip text={p.breached ? "Breached" : "Open"} /> : null}
    {!p.doneAt && p.minutesLeft !== null && p.minutesLeft !== undefined && <span className={p.minutesLeft < 0 ? x.late : x.muted}>{left(p.minutesLeft)}</span>}
  </div>
);

export const TicketInsight: React.FC<{ ticketId: number; ctx: PageCtx }> = ({ ticketId }) => {
  const sla = useLoad(() => api<SlaInfo>("GET", `/api/intake/tickets/${ticketId}/sla`), [ticketId]);
  const sug = useLoad(async () => asList<Article>(await api("GET", `/api/intake/tickets/${ticketId}/suggestions`)), [ticketId]);
  const s = sla.data;
  return (
    <div className={x.insight}>
      <div>
        <h4 className={x.h4}>Service targets</h4>
        <Wait loading={sla.loading && !s} error={sla.error} onRetry={sla.reload} />
        {s && (
          <>
            <SlaLine label="First response" p={s.response} />
            <SlaLine label="Resolution" p={s.resolution} />
            <div className={x.muted}>
              Priority {nice(s.priority)} · escalation level {s.escalationLevel}
              {s.breachedAt ? ` · missed on ${fmtStamp(s.breachedAt)}` : ""}
            </div>
          </>
        )}
      </div>
      <div>
        <h4 className={x.h4}>Articles that may help</h4>
        <Wait loading={sug.loading && !sug.data} error={sug.error} empty={!sug.loading && (sug.data ?? []).length === 0 && "No matching articles"} onRetry={sug.reload} />
        {(sug.data ?? []).map((a) => (
          <div key={a.id} className={x.article}>
            <div className={x.articleTitle}>
              {a.title} {!a.isPublished && <Chip text="Draft" />}
            </div>
            {a.snippet && <div className={x.muted}>{a.snippet}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ================================================================== *
 *  Service portal (requester view)
 * ================================================================== */
interface CatalogRow {
  id: number;
  name: string;
  category?: string | null;
  description?: string | null;
}
interface MyTicket {
  id: number;
  ticketNumber: number;
  title: string;
  ticketType: string;
  priority: string;
  status: string;
  createdAt: string;
  resolutionDueAt?: string | null;
}
const LEVELS = opts(["LOW", "MEDIUM", "HIGH"]);

export const PortalPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("request");
  const [pick, setPick] = useState<CatalogRow | "other" | null>(null);
  const [f, setF] = useState({ title: "", description: "", type: "REQUEST", urgency: "", impact: "" });
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const catalog = useLoad(async () => asList<CatalogRow>(await api("GET", "/api/intake/portal/catalog", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId]);
  const mine = useLoad(async () => asList<MyTicket>(await api("GET", "/api/intake/portal/tickets", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId]);
  const groups = useMemo(() => {
    const m = new Map<string, CatalogRow[]>();
    for (const c of catalog.data ?? []) m.set(c.category || "General", [...(m.get(c.category || "General") ?? []), c]);
    return [...m.entries()];
  }, [catalog.data]);

  const start = (p: CatalogRow | "other") => {
    setPick(p);
    setF({ title: p === "other" ? "" : p.name, description: "", type: "REQUEST", urgency: "", impact: "" });
  };
  const send = async () => {
    setBusy(true);
    const item = pick !== null && pick !== "other" ? pick : null;
    const ok = await act(
      () =>
        api("POST", "/api/intake/portal/tickets", {
          workspaceId: ctx.workspaceId,
          title: f.title.trim(),
          description: f.description.trim() || null,
          ticketType: f.type,
          catalogItemId: item ? item.id : null,
          urgency: f.urgency || null,
          impact: f.impact || null,
        }),
      "Request sent. You can follow it under My requests.",
    );
    setBusy(false);
    if (ok) {
      setPick(null);
      mine.reload();
      setTab("mine");
    }
  };

  return (
    <>
      <Head icon={<Globe20Regular />} title="Service portal" sub="Ask for help or request something from the team.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => start("other")}>
          Report a problem
        </Button>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="request">Request something</Tab>
          <Tab value="mine">My requests</Tab>
        </TabList>
      </div>
      <Page>
        {tab === "request" && (
          <>
            <Wait loading={catalog.loading && !catalog.data} error={catalog.error} empty={!catalog.loading && groups.length === 0 && "Nothing is in the catalog yet. Use “Report a problem” to write to the team."} onRetry={catalog.reload} />
            {groups.map(([cat, items]) => (
              <div key={cat} className={x.section}>
                <h3 className={x.h3}>{cat}</h3>
                <div className={x.cards}>
                  {items.map((c) => (
                    <button key={c.id} type="button" className={x.catCard} onClick={() => start(c)}>
                      <span className={x.catName}>{c.name}</span>
                      {c.description && <span className={x.muted}>{c.description}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
        {tab === "mine" && (
          <>
            <Wait loading={mine.loading && !mine.data} error={mine.error} empty={!mine.loading && (mine.data ?? []).length === 0 && "You haven’t sent any requests"} onRetry={mine.reload} />
            {(mine.data ?? []).map((t) => (
              <div key={t.id} className={x.ticket}>
                <button type="button" className={x.ticketHead} aria-expanded={openId === t.id} onClick={() => setOpenId(openId === t.id ? null : t.id)}>
                  <span className={x.strong}>
                    T-{t.ticketNumber} · {t.title}
                  </span>
                  <span className={x.chips}>
                    <Chip text={t.status} />
                    <Chip text={t.priority} />
                  </span>
                  <span className={x.muted}>Sent {fmtStamp(t.createdAt)}</span>
                </button>
                {openId === t.id && <TicketInsight ticketId={t.id} ctx={ctx} />}
              </div>
            ))}
          </>
        )}
      </Page>

      <FormDialog
        open={pick !== null}
        title={pick === "other" || pick === null ? "Report a problem" : pick.name}
        action="Send request"
        busy={busy}
        disabled={!f.title.trim()}
        onClose={() => setPick(null)}
        onSubmit={() => void send()}
      >
        <Field label="Summary" required>
          <Input value={f.title} onChange={(_, d) => setF({ ...f, title: d.value })} />
        </Field>
        <Field label="Details">
          <Textarea value={f.description} rows={4} resize="vertical" onChange={(_, d) => setF({ ...f, description: d.value })} />
        </Field>
        <div className={styles.formRow}>
          <Field label="Kind" className={styles.grow}>
            <Select value={f.type} onChange={(_, d) => setF({ ...f, type: d.value })}>
              <option value="REQUEST">Request</option>
              <option value="INCIDENT">Something is broken</option>
            </Select>
          </Field>
          <Field label="How urgent?" className={styles.grow}>
            <Select value={f.urgency} onChange={(_, d) => setF({ ...f, urgency: d.value })}>
              <option value="">Not sure</option>
              {LEVELS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="How many people?" className={styles.grow}>
            <Select value={f.impact} onChange={(_, d) => setF({ ...f, impact: d.value })}>
              <option value="">Not sure</option>
              <option value="LOW">Just me</option>
              <option value="MEDIUM">A team</option>
              <option value="HIGH">Many people</option>
            </Select>
          </Field>
        </div>
      </FormDialog>
    </>
  );
};

/* ================================================================== *
 *  Public form: renderer (also used for the preview)
 * ================================================================== */
interface PubField {
  id: number;
  label: string;
  type: string;
  required: boolean;
  options?: unknown;
  condition?: { fieldId?: number; op?: string; value?: unknown } | null;
}
interface PubForm {
  name: string;
  description?: string | null;
  fields: PubField[];
}
type AnswerValue = string | boolean | string[];

const optionList = (o: unknown): { value: string; label: string }[] =>
  Array.isArray(o) ? o.map((e) => (e && typeof e === "object" ? { value: str((e as { value?: unknown }).value), label: str((e as { label?: unknown; value?: unknown }).label ?? (e as { value?: unknown }).value) } : { value: str(e), label: str(e) })) : [];

const shownField = (f: PubField, v: Record<number, AnswerValue>) => {
  const c = f.condition;
  if (!c || typeof c.fieldId !== "number") return true;
  const have = v[c.fieldId];
  const flat = Array.isArray(have) ? have : have === undefined || have === "" ? [] : [String(have)];
  const want = c.value;
  const eq = (a: string, b: unknown) => a.toLowerCase() === String(b).toLowerCase();
  switch ((c.op ?? "eq").toLowerCase()) {
    case "notnull":
      return flat.length > 0;
    case "ne":
      return !flat.some((a) => eq(a, want));
    case "in":
      return Array.isArray(want) && flat.some((a) => want.some((w) => eq(a, w)));
    default:
      return flat.some((a) => eq(a, want));
  }
};

export const PublicFormView: React.FC<{ token: string; ctx: PageCtx }> = ({ token, ctx }) => {
  const form = useLoad(() => api<PubForm>("GET", `/api/public/forms/${encodeURIComponent(token)}`), [token]);
  const [vals, setVals] = useState<Record<number, AnswerValue>>({});
  const [email, setEmail] = useState(ctx.me.email ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ reference: string; message?: string } | null>(null);
  const [err, setErr] = useState("");
  const f = form.data;

  const submit = async () => {
    if (!f) return;
    setErr("");
    const values: Record<string, unknown> = {};
    for (const fld of f.fields) {
      if (!shownField(fld, vals)) continue;
      const v = vals[fld.id];
      const empty = v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
      if (fld.type === "CHECKBOX") {
        values[String(fld.id)] = v === true;
        continue;
      }
      if (empty) {
        if (fld.required) return setErr(`${fld.label} is required.`);
        continue;
      }
      values[String(fld.id)] = fld.type === "NUMBER" ? Number(v) : v;
    }
    setBusy(true);
    try {
      const r = await api<{ reference: string; message?: string }>("POST", `/api/public/forms/${encodeURIComponent(token)}/submit`, { values, email: email.trim() || undefined, website: "" });
      setDone(r);
    } catch (e) {
      setErr(msgOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className={x.formDone}>
        <h3 className={x.h3}>Thank you</h3>
        <p>{done.message || `Your answers were sent. Reference ${done.reference}.`}</p>
        <Button
          onClick={() => {
            setDone(null);
            setVals({});
          }}
        >
          Send another
        </Button>
      </div>
    );
  return (
    <div className={x.publicForm}>
      <Wait loading={form.loading && !f} error={form.error} onRetry={form.reload} />
      {f && (
        <>
          <h3 className={x.h3}>{f.name}</h3>
          {f.description && <p className={x.muted}>{f.description}</p>}
          {f.fields
            .filter((fld) => shownField(fld, vals))
            .map((fld) => {
              const v = vals[fld.id];
              const set = (nv: AnswerValue) => setVals({ ...vals, [fld.id]: nv });
              const o = optionList(fld.options);
              const common = { label: fld.label, required: fld.required };
              if (fld.type === "TEXTAREA")
                return (
                  <Field key={fld.id} {...common}>
                    <Textarea rows={4} resize="vertical" value={typeof v === "string" ? v : ""} onChange={(_, d) => set(d.value)} />
                  </Field>
                );
              if (fld.type === "CHECKBOX") return <Checkbox key={fld.id} label={fld.label} checked={v === true} onChange={(_, d) => set(d.checked === true)} />;
              if (fld.type === "SELECT")
                return (
                  <Field key={fld.id} {...common}>
                    <Select value={typeof v === "string" ? v : ""} onChange={(_, d) => set(d.value)}>
                      <option value="">Choose…</option>
                      {o.map((e) => (
                        <option key={e.value} value={e.value}>
                          {e.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                );
              if (fld.type === "MULTISELECT") {
                const cur = Array.isArray(v) ? v : [];
                return (
                  <Field key={fld.id} {...common}>
                    <div className={x.checks}>
                      {o.map((e) => (
                        <Checkbox key={e.value} label={e.label} checked={cur.includes(e.value)} onChange={(_, d) => set(d.checked === true ? [...cur, e.value] : cur.filter((c) => c !== e.value))} />
                      ))}
                    </div>
                  </Field>
                );
              }
              const type = fld.type === "EMAIL" ? "email" : fld.type === "NUMBER" ? "number" : fld.type === "DATE" ? "date" : fld.type === "URL" ? "url" : fld.type === "PHONE" ? "tel" : "text";
              return (
                <Field key={fld.id} {...common}>
                  <Input type={type} value={typeof v === "string" ? v : ""} onChange={(_, d) => set(d.value)} />
                </Field>
              );
            })}
          <Field label="Your e-mail" hint="So the team can reach you.">
            <Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} />
          </Field>
          {err && <div className={x.errText}>{err}</div>}
          <div>
            <Button className={styles.primary} appearance="primary" disabled={busy} onClick={() => void submit()}>
              {busy ? "Sending…" : "Submit"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/* ================================================================== *
 *  Forms manager (form + fields + submissions)
 * ================================================================== */
const FIELD_TYPES = ["TEXT", "TEXTAREA", "EMAIL", "NUMBER", "DATE", "SELECT", "MULTISELECT", "CHECKBOX", "URL", "PHONE"];
const MAPS_TO = ["Title", "Description", "DueDate", "Priority", "Email", "Urgency", "Impact"];

const FormFields: React.FC<{ ctx: PageCtx; form: Row }> = ({ ctx, form }) => {
  const list = useLoad(async () => (await crud.list<Row>("intake/frm/formfield", { formId: form.id, sort: "sortOrder" })).items, [form.id]);
  const rows = list.data ?? [];
  const fields: FieldDef[] = useMemo(
    () => [
      { key: "label", label: "Question", kind: "text", required: true },
      { key: "fieldType", label: "Answer type", kind: "select", required: true, options: opts(FIELD_TYPES), initial: "TEXT" },
      { key: "isRequired", label: "Must be answered", kind: "bool" },
      { key: "optionsJson", label: "Choices (JSON list, for the select types)", kind: "json", hint: 'For example ["Email","Phone"]' },
      { key: "mapsToField", label: "Fills this field of the new item", kind: "select", options: opts(MAPS_TO) },
      { key: "conditionJson", label: "Only show when (JSON)", kind: "json", hint: 'For example {"fieldId": 12, "op": "eq", "value": "Yes"}' },
      { key: "sortOrder", label: "Order", kind: "number", initial: String((rows.length + 1) * 10) },
    ],
    [rows.length],
  );
  return (
    <MiniCrud
      ctx={ctx}
      route="intake/frm/formfield"
      noun="question"
      fields={fields}
      query={{ formId: form.id }}
      extraCreate={{ formId: form.id }}
      sort="sortOrder"
      cols={[
        { key: "sortOrder", label: "#" },
        { key: "label", label: "Question" },
        { key: "fieldType", label: "Type", render: (r) => <Chip text={str(r.fieldType)} /> },
        { key: "isRequired", label: "Required", render: (r) => (r.isRequired === true ? "Yes" : "No") },
        { key: "mapsToField", label: "Fills" },
      ]}
    />
  );
};

const FormSubmissions: React.FC<{ ctx: PageCtx; form: Row }> = ({ ctx, form }) => {
  const list = useLoad(async () => (await crud.list<Row>("intake/frm/formsubmission", { formId: form.id, sort: "-submittedAt", pageSize: 100 })).items, [form.id]);
  const [shown, setShown] = useState<Row | null>(null);
  const rows = list.data ?? [];
  return (
    <div className={x.section}>
      <Wait loading={list.loading && !list.data} error={list.error} empty={!list.loading && rows.length === 0 && "No submissions yet"} onRetry={list.reload} />
      {rows.length > 0 && (
        <table className={x.tbl}>
          <thead>
            <tr>
              <th>Received</th>
              <th>From</th>
              <th>Status</th>
              <th>Made</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{fmtStamp(str(r.submittedAt))}</td>
                <td>{str(r.submittedByEmail) || "—"}</td>
                <td>
                  <Chip text={str(r.status)} />
                </td>
                <td>
                  {r.taskId ? (
                    <Button size="small" appearance="transparent" onClick={() => ctx.openTask(Number(r.taskId))}>
                      Task #{str(r.taskId)}
                    </Button>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <Button size="small" icon={<Eye20Regular />} onClick={() => setShown(r)}>
                    Answers
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Dialog open={shown !== null} onOpenChange={(_, o) => !o.open && setShown(null)}>
        <DialogSurface className={styles.dialog}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Submitted answers
            </DialogTitle>
            <DialogContent className={styles.formBody}>
              {(() => {
                const p = parseJson(shown?.payloadJson);
                const items = Array.isArray(p) ? (p as { label?: unknown; value?: unknown }[]) : [];
                return items.length === 0 ? (
                  <pre className={x.pre}>{pretty(shown?.payloadJson)}</pre>
                ) : (
                  items.map((it, i) => (
                    <div key={i} className={x.answer}>
                      <div className={x.answerQ}>{str(it.label)}</div>
                      <div>{Array.isArray(it.value) ? it.value.join(", ") : it.value === true ? "Yes" : it.value === false ? "No" : str(it.value)}</div>
                    </div>
                  ))
                );
              })()}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

const FormsManager: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [sel, setSel] = useState<Row | null>(null);
  const [section, setSection] = useState("fields");
  const [preview, setPreview] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<Row | null>(null);
  const [f, setF] = useState({ name: "", description: "", projectId: "", target: "TASK", ticketType: "REQUEST", assigneeId: "", isPublic: true, isActive: true });
  const [busy, setBusy] = useState(false);
  const list = useLoad(async () => (await crud.list<Row>("intake/frm/form", { workspaceId: ctx.workspaceId, sort: "-id" })).items, [ctx.workspaceId]);
  const rows = list.data ?? [];
  const link = (token: unknown) => `${TASKS_API}/api/public/forms/${str(token)}`;
  const routeOf = (r: Row) => {
    const j = parseJson(r.routingJson) as { target?: string } | null;
    return (j?.target ?? "TASK").toUpperCase();
  };

  const startNew = () => {
    setEdit(null);
    setF({ name: "", description: "", projectId: ctx.projectId === null ? "" : String(ctx.projectId), target: "TASK", ticketType: "REQUEST", assigneeId: "", isPublic: true, isActive: true });
    setOpen(true);
  };
  const startEdit = (r: Row) => {
    const j = (parseJson(r.routingJson) ?? {}) as { target?: string; ticketType?: string; assigneeId?: number | string };
    setEdit(r);
    setF({
      name: str(r.name),
      description: str(r.description),
      projectId: str(r.projectId),
      target: (j.target ?? "TASK").toUpperCase(),
      ticketType: j.ticketType ?? "REQUEST",
      assigneeId: str(r.defaultAssigneeId),
      isPublic: r.isPublic === true,
      isActive: r.isActive !== false,
    });
    setOpen(true);
  };
  const save = async () => {
    setBusy(true);
    const routing: Record<string, unknown> = { target: f.target };
    if (f.target === "TICKET") routing.ticketType = f.ticketType;
    const body = {
      name: f.name.trim(),
      description: f.description.trim() || null,
      projectId: f.projectId ? Number(f.projectId) : null,
      defaultAssigneeId: f.assigneeId ? Number(f.assigneeId) : null,
      routingJson: JSON.stringify(routing),
      isPublic: f.isPublic,
      isActive: f.isActive,
    };
    const ok = await act(() => (edit ? crud.update("intake/frm/form", edit.id, body) : crud.create("intake/frm/form", { workspaceId: ctx.workspaceId, ...body })), edit ? "Form saved." : "Form created.");
    setBusy(false);
    if (ok) {
      setOpen(false);
      list.reload();
    }
  };

  return (
    <div className={x.section}>
      <div className={x.sectionBar}>
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={startNew}>
          New form
        </Button>
      </div>
      <Wait loading={list.loading && !list.data} error={list.error} empty={!list.loading && rows.length === 0 && "No forms yet"} onRetry={list.reload} />
      {rows.map((r) => (
        <div key={r.id} className={`${x.formCard} ${sel?.id === r.id ? x.picked : ""}`}>
          <div className={x.formTop}>
            <button type="button" className={x.formName} onClick={() => setSel(sel?.id === r.id ? null : r)}>
              <span className={x.strong}>{str(r.name)}</span>
              <span className={x.muted}>
                Creates a {routeOf(r) === "TICKET" ? "ticket" : "task"} · {r.isPublic === true ? "public link on" : "not public"}
              </span>
            </button>
            <div className={x.rowActions}>
              {r.isActive === true ? <Chip text="Active" /> : <Chip text="Off" />}
              <Button size="small" icon={<Eye20Regular />} disabled={!r.publicToken} onClick={() => setPreview(str(r.publicToken))}>
                Preview
              </Button>
              <Button size="small" icon={<Copy20Regular />} disabled={!r.publicToken} onClick={() => copy(link(r.publicToken), ctx.notify)}>
                Copy address
              </Button>
              <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label="Edit form" onClick={() => startEdit(r)} />
              <Button
                size="small"
                appearance="subtle"
                icon={<Delete20Regular />}
                aria-label="Delete form"
                onClick={async () => {
                  if (await act(() => crud.remove("intake/frm/form", r.id), "Form deleted.")) {
                    if (sel?.id === r.id) setSel(null);
                    list.reload();
                  }
                }}
              />
            </div>
          </div>
          {r.publicToken ? <div className={x.addr}>{link(r.publicToken)}</div> : null}
          {sel?.id === r.id && (
            <>
              <TabList size="small" selectedValue={section} onTabSelect={(_, d) => setSection(String(d.value))}>
                <Tab value="fields">Questions</Tab>
                <Tab value="subs">Submissions</Tab>
              </TabList>
              {section === "fields" ? <FormFields ctx={ctx} form={r} /> : <FormSubmissions ctx={ctx} form={r} />}
            </>
          )}
        </div>
      ))}

      <FormDialog open={open} title={edit ? "Edit form" : "New form"} action={edit ? "Save" : "Create form"} busy={busy} disabled={!f.name.trim() || (f.target === "TASK" && !f.projectId)} onClose={() => setOpen(false)} onSubmit={() => void save()}>
        <Field label="Name" required>
          <Input value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
        </Field>
        <Field label="Description">
          <Textarea value={f.description} rows={2} resize="vertical" onChange={(_, d) => setF({ ...f, description: d.value })} />
        </Field>
        <div className={styles.formRow}>
          <Field label="Each answer becomes a" className={styles.grow}>
            <Select value={f.target} onChange={(_, d) => setF({ ...f, target: d.value })}>
              <option value="TASK">Task</option>
              <option value="TICKET">Service ticket</option>
            </Select>
          </Field>
          {f.target === "TICKET" && (
            <Field label="Ticket type" className={styles.grow}>
              <Select value={f.ticketType} onChange={(_, d) => setF({ ...f, ticketType: d.value })}>
                {["INCIDENT", "REQUEST", "PROBLEM", "CHANGE"].map((t) => (
                  <option key={t} value={t}>
                    {nice(t)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <Field label="Project" required={f.target === "TASK"} hint={f.target === "TASK" ? "Tasks are created in this project." : null}>
          <Select value={f.projectId} onChange={(_, d) => setF({ ...f, projectId: d.value })}>
            <option value="">None</option>
            {ctx.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Assign to">
          <Select value={f.assigneeId} onChange={(_, d) => setF({ ...f, assigneeId: d.value })}>
            <option value="">Workspace admins are told</option>
            {ctx.people.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Switch label="Anyone with the address can fill it in" checked={f.isPublic} onChange={(_, d) => setF({ ...f, isPublic: d.checked })} />
        <Switch label="Accepting answers" checked={f.isActive} onChange={(_, d) => setF({ ...f, isActive: d.checked })} />
      </FormDialog>

      <Dialog open={preview !== null} onOpenChange={(_, o) => !o.open && setPreview(null)}>
        <DialogSurface className={styles.dialog}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Form preview
            </DialogTitle>
            <DialogContent className={styles.formBody}>
              <p className={x.muted}>This is what people see. Submitting here sends a real answer.</p>
              {preview && <PublicFormView token={preview} ctx={ctx} />}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

/* ================================================================== *
 *  Service desk admin
 * ================================================================== */
const PriorityMatrixEditor: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const list = useLoad(async () => (await crud.list<Row>("intake/svc/prioritymatrix", { workspaceId: ctx.workspaceId })).items, [ctx.workspaceId]);
  const rows = list.data ?? [];
  const cell = (u: string, i: string) => rows.find((r) => str(r.urgency).toUpperCase() === u && str(r.impact).toUpperCase() === i);
  const setCell = async (u: string, i: string, priority: string) => {
    const cur = cell(u, i);
    const ok = await act(async () => {
      if (!priority) {
        if (cur) await crud.remove("intake/svc/prioritymatrix", cur.id);
      } else if (cur) await crud.update("intake/svc/prioritymatrix", cur.id, { priority });
      else await crud.create("intake/svc/prioritymatrix", { workspaceId: ctx.workspaceId, urgency: u, impact: i, priority });
    });
    if (ok) list.reload();
  };
  return (
    <div className={x.section}>
      <p className={x.muted}>When a ticket says how urgent it is and how many people it affects, this table decides its priority.</p>
      <Wait loading={list.loading && !list.data} error={list.error} onRetry={list.reload} />
      <div className={x.scroll}>
        <table className={x.tbl}>
          <thead>
            <tr>
              <th>Urgency ↓ / Impact →</th>
              {["LOW", "MEDIUM", "HIGH"].map((i) => (
                <th key={i}>{nice(i)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {["HIGH", "MEDIUM", "LOW"].map((u) => (
              <tr key={u}>
                <td className={x.strong}>{nice(u)}</td>
                {["LOW", "MEDIUM", "HIGH"].map((i) => (
                  <td key={i}>
                    <Select aria-label={`Priority for urgency ${u} and impact ${i}`} value={str(cell(u, i)?.priority).toUpperCase()} onChange={(_, d) => void setCell(u, i, d.value)}>
                      <option value="">Not set</option>
                      {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((p) => (
                        <option key={p} value={p}>
                          {nice(p)}
                        </option>
                      ))}
                    </Select>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ServiceAdminPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("sla");
  const policies = useLoad(async () => (await crud.list<Row>("intake/svc/slapolicy", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId, tab]);
  const forms = useLoad(async () => (await crud.list<Row>("intake/frm/form", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId, tab]);
  const polOpts = (policies.data ?? []).map((p) => ({ value: String(p.id), label: str(p.name) }));
  const formOpts = (forms.data ?? []).map((p) => ({ value: String(p.id), label: str(p.name) }));
  const polName = (id: unknown) => polOpts.find((o) => o.value === str(id))?.label ?? (id ? `Policy ${str(id)}` : "—");

  const slaFields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "responseMinutes", label: "First response within (minutes)", kind: "number", required: true, initial: "60" },
    { key: "resolutionMinutes", label: "Resolution within (minutes)", kind: "number", required: true, initial: "480" },
    { key: "priorityMatch", label: "Applies to priority", kind: "select", options: opts(["LOW", "MEDIUM", "HIGH", "CRITICAL"]), hint: "Leave empty to apply to every priority." },
    { key: "businessHoursJson", label: "Business hours (JSON, optional)", kind: "json" },
    { key: "isActive", label: "Active", kind: "bool", initial: true },
  ];
  const escFields: FieldDef[] = [
    { key: "slaPolicyId", label: "SLA policy", kind: "select", required: true, options: polOpts },
    { key: "afterMinutes", label: "After (minutes without an answer)", kind: "number", required: true, initial: "30" },
    { key: "action", label: "Then", kind: "select", required: true, options: [{ value: "NOTIFY", label: "Notify" }, { value: "REASSIGN", label: "Reassign" }, { value: "RAISE_PRIORITY", label: "Raise priority" }], initial: "NOTIFY" },
    { key: "targetUserId", label: "Person to tell or hand it to", kind: "user" },
  ];
  const catFields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "category", label: "Group", kind: "text" },
    { key: "description", label: "Description", kind: "textarea" },
    { key: "formId", label: "Form to fill in", kind: "select", options: formOpts },
    { key: "slaPolicyId", label: "SLA policy", kind: "select", options: polOpts },
    { key: "isActive", label: "Shown in the portal", kind: "bool", initial: true },
  ];

  return (
    <>
      <Head icon={<Wrench20Regular />} title="Service desk admin" sub="Response targets, escalation, priorities, the catalog and intake forms." />
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="sla">SLA policies</Tab>
          <Tab value="esc">Escalation</Tab>
          <Tab value="matrix">Priority matrix</Tab>
          <Tab value="catalog">Catalog</Tab>
          <Tab value="forms">Forms</Tab>
        </TabList>
      </div>
      <Page>
        {tab === "sla" && (
          <MiniCrud
            ctx={ctx}
            route="intake/svc/slapolicy"
            noun="SLA policy"
            fields={slaFields}
            sort="name"
            cols={[
              { key: "name", label: "Name" },
              { key: "responseMinutes", label: "Respond within", render: (r) => `${str(r.responseMinutes)} min` },
              { key: "resolutionMinutes", label: "Resolve within", render: (r) => `${str(r.resolutionMinutes)} min` },
              { key: "priorityMatch", label: "Priority", render: (r) => (r.priorityMatch ? nice(str(r.priorityMatch)) : "All") },
              { key: "isActive", label: "Status", render: (r) => yesNo(r.isActive) },
            ]}
          />
        )}
        {tab === "esc" && (
          <MiniCrud
            ctx={ctx}
            route="intake/svc/escalationrule"
            noun="escalation rule"
            fields={escFields}
            reloadKey={polOpts.length}
            cols={[
              { key: "slaPolicyId", label: "Policy", render: (r) => polName(r.slaPolicyId) },
              { key: "afterMinutes", label: "After", render: (r) => `${str(r.afterMinutes)} min` },
              { key: "action", label: "Then", render: (r) => <Chip text={str(r.action)} /> },
              { key: "targetUserId", label: "Person", render: (r) => userName(ctx, r.targetUserId) },
            ]}
          />
        )}
        {tab === "matrix" && <PriorityMatrixEditor ctx={ctx} />}
        {tab === "catalog" && (
          <MiniCrud
            ctx={ctx}
            route="intake/svc/catalogitem"
            noun="catalog item"
            fields={catFields}
            sort="name"
            cols={[
              { key: "name", label: "Name" },
              { key: "category", label: "Group" },
              { key: "formId", label: "Form", render: (r) => formOpts.find((o) => o.value === str(r.formId))?.label ?? "—" },
              { key: "slaPolicyId", label: "SLA", render: (r) => polName(r.slaPolicyId) },
              { key: "isActive", label: "Status", render: (r) => yesNo(r.isActive) },
            ]}
          />
        )}
        {tab === "forms" && <FormsManager ctx={ctx} />}
      </Page>
    </>
  );
};

/* ================================================================== *
 *  Workflows, rule tools, recurrence, approvals
 * ================================================================== */
interface Approval {
  id: number;
  instanceId?: number | null;
  taskId?: number | null;
  taskTitle?: string | null;
  status: string;
  stepKey?: string | null;
  dueAt?: string | null;
  createdAt: string;
}
interface InstanceInfo {
  instance: { id: number; workflowDefId: number; taskId?: number | null; status: string; tokens?: { id: string; step: string; state: string }[] | null; history?: string[] | null };
  approvals: { id: number; stepKey?: string | null; approverUserId: number; status: string; decisionAt?: string | null; comment?: string | null; dueAt?: string | null }[];
}
interface AutoCatalog {
  eventTriggers: string[];
  scheduledTriggers: string[];
  actions: string[];
  settableFields: string[];
  filterFields: string[];
  workflowSteps: string[];
}

const WORKFLOW_SAMPLE = JSON.stringify(
  {
    start: "check",
    steps: [
      { key: "check", type: "condition", if: { Priority: "HIGH" }, then: "approve", else: "done" },
      { key: "approve", type: "approval", approvers: ["project_owner"], dueDays: 2, onApprove: "tell", onReject: "done" },
      { key: "tell", type: "notify", users: ["assignees"], title: "Approved", next: "done" },
      { key: "done", type: "end" },
    ],
  },
  null,
  2,
);

const InstanceDialog: React.FC<{ id: number | null; ctx: PageCtx; onClose: () => void; onChanged: () => void }> = ({ id, ctx, onClose, onChanged }) => {
  const act = useAct(ctx.notify);
  const info = useLoad(() => (id === null ? Promise.resolve(null) : api<InstanceInfo>("GET", `/api/automation/workflows/instances/${id}`)), [id]);
  const d = info.data;
  return (
    <Dialog open={id !== null} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Workflow run #{id}
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <Wait loading={info.loading && !d} error={info.error} onRetry={info.reload} />
            {d && (
              <>
                <div className={x.chips}>
                  <Chip text={d.instance.status} />
                  {d.instance.taskId ? (
                    <Button size="small" appearance="transparent" onClick={() => ctx.openTask(Number(d.instance.taskId))}>
                      Task #{d.instance.taskId}
                    </Button>
                  ) : null}
                </div>
                <h4 className={x.h4}>Where it is now</h4>
                {(d.instance.tokens ?? []).length === 0 ? <div className={x.muted}>No active step</div> : (d.instance.tokens ?? []).map((t) => <div key={t.id}>{t.step} · {nice(t.state)}</div>)}
                <h4 className={x.h4}>History</h4>
                <ol className={x.history}>
                  {(d.instance.history ?? []).map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ol>
                {d.approvals.length > 0 && (
                  <>
                    <h4 className={x.h4}>Approvals</h4>
                    {d.approvals.map((a) => (
                      <div key={a.id} className={x.answer}>
                        <div className={x.answerQ}>
                          {a.stepKey ?? "Approval"} · {userName(ctx, a.approverUserId)} · <Chip text={a.status} />
                        </div>
                        {a.comment && <div>{a.comment}</div>}
                        <div className={x.muted}>{a.decisionAt ? `Decided ${fmtStamp(a.decisionAt)}` : a.dueAt ? `Due ${fmtStamp(a.dueAt)}` : ""}</div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {d?.instance.status === "RUNNING" && (
              <Button
                onClick={async () => {
                  if (id !== null && (await act(() => api("POST", `/api/automation/workflows/instances/${id}/cancel`), "Run cancelled."))) {
                    info.reload();
                    onChanged();
                  }
                }}
              >
                Cancel this run
              </Button>
            )}
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Close</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const ApprovalsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [status, setStatus] = useState("PENDING");
  const [deciding, setDeciding] = useState<{ a: Approval; decision: "APPROVED" | "REJECTED" } | null>(null);
  const [comment, setComment] = useState("");
  const [inst, setInst] = useState<number | null>(null);
  const list = useLoad(async () => asList<Approval>(await api("GET", "/api/automation/approvals/mine", undefined, { status })), [status]);
  const rows = list.data ?? [];
  return (
    <div className={x.section}>
      <TabList size="small" selectedValue={status} onTabSelect={(_, d) => setStatus(String(d.value))}>
        <Tab value="PENDING">Waiting for me</Tab>
        <Tab value="APPROVED">Approved</Tab>
        <Tab value="REJECTED">Rejected</Tab>
      </TabList>
      <Wait loading={list.loading && !list.data} error={list.error} empty={!list.loading && rows.length === 0 && "Nothing here"} onRetry={list.reload} />
      {rows.map((a) => (
        <div key={a.id} className={x.approval}>
          <div className={x.grow}>
            <div className={x.strong}>{a.taskTitle ?? `Approval #${a.id}`}</div>
            <div className={x.muted}>
              {a.stepKey ? `${a.stepKey} · ` : ""}asked {fmtStamp(a.createdAt)}
              {a.dueAt ? ` · due ${fmtStamp(a.dueAt)}` : ""}
            </div>
          </div>
          {a.taskId ? (
            <Button size="small" onClick={() => ctx.openTask(Number(a.taskId))}>
              Open task
            </Button>
          ) : null}
          {a.instanceId ? (
            <Button size="small" onClick={() => setInst(Number(a.instanceId))}>
              Workflow
            </Button>
          ) : null}
          {a.status === "PENDING" && (
            <>
              <Button size="small" appearance="primary" className={styles.primary} onClick={() => (setDeciding({ a, decision: "APPROVED" }), setComment(""))}>
                Approve
              </Button>
              <Button size="small" onClick={() => (setDeciding({ a, decision: "REJECTED" }), setComment(""))}>
                Reject
              </Button>
            </>
          )}
        </div>
      ))}
      <FormDialog
        open={deciding !== null}
        title={deciding ? `${deciding.decision === "APPROVED" ? "Approve" : "Reject"}: ${deciding.a.taskTitle ?? `#${deciding.a.id}`}` : "Decide"}
        action={deciding?.decision === "APPROVED" ? "Approve" : "Reject"}
        onClose={() => setDeciding(null)}
        onSubmit={async () => {
          if (!deciding) return;
          if (await act(() => api("POST", `/api/automation/approvals/${deciding.a.id}/decide`, { decision: deciding.decision, comment: comment.trim() || null }), "Your decision was recorded.")) {
            setDeciding(null);
            list.reload();
          }
        }}
      >
        <Field label="Comment (optional)">
          <Textarea value={comment} rows={3} resize="vertical" onChange={(_, d) => setComment(d.value)} />
        </Field>
      </FormDialog>
      <InstanceDialog id={inst} ctx={ctx} onClose={() => setInst(null)} onChanged={list.reload} />
    </div>
  );
};

const DefinitionsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const tasks = useTaskOptions(ctx);
  const [startFor, setStartFor] = useState<Row | null>(null);
  const [taskId, setTaskId] = useState("");
  const [inst, setInst] = useState<number | null>(null);
  const [key, setKey] = useState(0);
  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "description", label: "Description", kind: "textarea" },
    { key: "status", label: "Status", kind: "select", required: true, options: opts(["DRAFT", "PUBLISHED", "RETIRED"]), initial: "DRAFT" },
    { key: "triggerType", label: "Starts automatically on", kind: "text", hint: "Optional, for example TASK_CREATED." },
    { key: "definitionJson", label: "Steps (JSON)", kind: "json", required: true, initial: WORKFLOW_SAMPLE, hint: "Step types: notify, set_field, assign, comment, create_task, condition, approval, parallel, join, webhook, end. The server checks it when you save." },
  ];
  const instances = useLoad(async () => (await crud.list<Row>("automation/wf/workflowinstance", { workspaceId: ctx.workspaceId, sort: "-id", pageSize: 50 })).items, [ctx.workspaceId, key]);
  const names = useLoad(async () => (await crud.list<Row>("automation/wf/workflowdef", { workspaceId: ctx.workspaceId, pageSize: 200 })).items, [ctx.workspaceId, key]);
  const defName = (id: unknown) => str((names.data ?? []).find((d) => d.id === Number(id))?.name) || `Workflow ${str(id)}`;
  return (
    <>
      <MiniCrud
        ctx={ctx}
        route="automation/wf/workflowdef"
        noun="workflow"
        fields={fields}
        reloadKey={key}
        onChanged={() => setKey((k) => k + 1)}
        cols={[
          { key: "name", label: "Name" },
          { key: "version", label: "Version", render: (r) => `v${str(r.version)}` },
          { key: "status", label: "Status", render: (r) => <Chip text={str(r.status)} /> },
          {
            key: "steps",
            label: "Steps",
            render: (r) => {
              const j = parseJson(r.definitionJson) as { steps?: { key: string; type: string }[] } | null;
              return (j?.steps ?? []).map((s) => s.type).join(" → ") || "—";
            },
          },
        ]}
        rowActions={(r) => (
          <Button size="small" icon={<Play20Regular />} disabled={r.status !== "PUBLISHED"} title={r.status === "PUBLISHED" ? "Start a run" : "Publish the workflow first"} onClick={() => (setStartFor(r), setTaskId(""))}>
            Start
          </Button>
        )}
      />
      <div className={x.section}>
        <h3 className={x.h3}>Recent runs</h3>
        <Wait loading={instances.loading && !instances.data} error={instances.error} empty={!instances.loading && (instances.data ?? []).length === 0 && "No runs yet"} onRetry={instances.reload} />
        {(instances.data ?? []).length > 0 && (
          <table className={x.tbl}>
            <thead>
              <tr>
                <th>Run</th>
                <th>Workflow</th>
                <th>Status</th>
                <th>Step</th>
                <th>Started</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(instances.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td>#{r.id}</td>
                  <td>{defName(r.workflowDefId)}</td>
                  <td>
                    <Chip text={str(r.status)} />
                  </td>
                  <td>{str(r.currentStep) || "—"}</td>
                  <td>{fmtStamp(str(r.startedAt))}</td>
                  <td>
                    <Button size="small" onClick={() => setInst(r.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <FormDialog
        open={startFor !== null}
        title={startFor ? `Start “${str(startFor.name)}”` : "Start workflow"}
        action="Start"
        onClose={() => setStartFor(null)}
        onSubmit={async () => {
          if (!startFor) return;
          let created: { id?: number } | undefined;
          const ok = await act(async () => {
            created = await api<{ id?: number }>("POST", `/api/automation/workflows/${startFor.id}/start`, { taskId: taskId ? Number(taskId) : null });
          }, "Workflow started.");
          if (ok) {
            setStartFor(null);
            setKey((k) => k + 1);
            if (created?.id) setInst(created.id);
          }
        }}
      >
        <TaskSelect tasks={tasks} value={taskId} onChange={setTaskId} label="Run it on this task" optional />
      </FormDialog>
      <InstanceDialog id={inst} ctx={ctx} onClose={() => setInst(null)} onChanged={() => setKey((k) => k + 1)} />
    </>
  );
};

interface RuleRun {
  id: number;
  taskId?: number | null;
  startedAt: string;
  status: string;
  errorMessage?: string | null;
  durationMs?: number | null;
}
const RuleToolsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const tasks = useTaskOptions(ctx);
  const [ruleId, setRuleId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [out, setOut] = useState<{ kind: string; text: string } | null>(null);
  const rules = useLoad(async () => (await crud.list<Row>("automation/auto/rule", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId]);
  const runs = useLoad(async () => (ruleId ? asList<RuleRun>(await api("GET", `/api/automation/rules/${ruleId}/runs`, undefined, { limit: 50 })) : []), [ruleId]);
  const isAdmin = ctx.me.workspaces.find((w:any) => w.id === ctx.workspaceId)?.role === "ADMIN";

  return (
    <div className={x.section}>
      <div className={x.toolRow}>
        <Field label="Rule" className={styles.grow}>
          <Select value={ruleId} onChange={(_, d) => (setRuleId(d.value), setOut(null))}>
            <option value="">Choose a rule</option>
            {(rules.data ?? []).map((r) => (
              <option key={r.id} value={String(r.id)}>
                {str(r.name)}
              </option>
            ))}
          </Select>
        </Field>
        <div className={styles.grow}>
          <TaskSelect tasks={tasks} value={taskId} onChange={setTaskId} label="Try it on" />
        </div>
      </div>
      <div className={x.rowActions}>
        <Button
          disabled={!ruleId || !taskId}
          onClick={async () => {
            await act(async () => {
              const r = await api<{ matches: boolean; actions?: unknown }>("POST", `/api/automation/rules/${ruleId}/test`, { taskId: Number(taskId) });
              setOut(r.matches ? { kind: "ok", text: `The rule matches this task. It would run:\n${pretty(r.actions)}` } : { kind: "no", text: "The rule does not match this task, so nothing would run." });
            });
          }}
        >
          Test (changes nothing)
        </Button>
        <Button
          appearance="primary"
          className={styles.primary}
          disabled={!ruleId || !taskId || !isAdmin}
          title={isAdmin ? "Run the rule on this task now" : "Only workspace admins can run a rule"}
          onClick={async () => {
            await act(async () => {
              const r = await api<{ ran: boolean }>("POST", `/api/automation/rules/${ruleId}/run`, { taskId: Number(taskId) });
              setOut({ kind: r.ran ? "ok" : "no", text: r.ran ? "The rule ran on this task." : "The rule did not run because its condition did not match." });
              runs.reload();
            });
          }}
        >
          Run now
        </Button>
      </div>
      {out && <pre className={`${x.pre} ${out.kind === "ok" ? x.ok : ""}`}>{out.text}</pre>}
      {ruleId && (
        <>
          <h3 className={x.h3}>Recent runs</h3>
          <Wait loading={runs.loading && !runs.data} error={runs.error} empty={!runs.loading && (runs.data ?? []).length === 0 && "This rule has not run yet"} onRetry={runs.reload} />
          {(runs.data ?? []).length > 0 && (
            <table className={x.tbl}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Task</th>
                  <th>Result</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>{fmtStamp(r.startedAt)}</td>
                    <td>
                      {r.taskId ? (
                        <Button size="small" appearance="transparent" onClick={() => ctx.openTask(Number(r.taskId))}>
                          #{r.taskId}
                        </Button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <Chip text={r.status} />
                      {r.errorMessage ? <span className={x.late}> {r.errorMessage}</span> : null}
                    </td>
                    <td>{r.durationMs ?? 0} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const RecurrenceTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const tasks = useTaskOptions(ctx);
  const [rrule, setRrule] = useState("FREQ=WEEKLY;BYDAY=MO,WE");
  const [from, setFrom] = useState(() => iso(new Date()));
  const [count, setCount] = useState("10");
  const [dates, setDates] = useState<string[] | null>(null);
  const [normal, setNormal] = useState("");
  const [err, setErr] = useState("");
  const preview = useCallback(async () => {
    setErr("");
    try {
      const r = await api<{ rrule: string; dates: string[] }>("POST", "/api/automation/recurrence/preview", { rrule: rrule.trim(), from, count: Number(count) || 10 });
      setDates(r.dates);
      setNormal(r.rrule);
    } catch (e) {
      setDates(null);
      setErr(e instanceof ApiError && e.status === 400 ? "That repeat rule isn’t valid. Example: FREQ=WEEKLY;BYDAY=MO,WE" : msgOf(e));
    }
  }, [rrule, from, count]);
  const fields: FieldDef[] = [
    { key: "templateTaskId", label: "Task to repeat", kind: "select", required: true, options: tasks.map((t) => ({ value: String(t.id), label: `#${t.id} · ${t.title}` })) },
    { key: "rrule", label: "Repeats (RRULE)", kind: "text", required: true, initial: "FREQ=WEEKLY;BYDAY=MO" },
    { key: "createAheadDays", label: "Create it this many days ahead (0–90)", kind: "number", initial: "0" },
    { key: "endsOn", label: "Ends on", kind: "date" },
    { key: "isActive", label: "Active", kind: "bool", initial: true },
  ];
  return (
    <>
      <div className={x.section}>
        <h3 className={x.h3}>Repeat rule preview</h3>
        <p className={x.muted}>Type a repeat rule and see the next dates it produces.</p>
        <div className={x.toolRow}>
          <Field label="Rule" className={styles.grow}>
            <Input value={rrule} onChange={(_, d) => setRrule(d.value)} />
          </Field>
          <Field label="Starting from">
            <Input type="date" value={from} onChange={(_, d) => setFrom(d.value)} />
          </Field>
          <Field label="How many">
            <Input type="number" min="1" max="50" value={count} onChange={(_, d) => setCount(d.value)} />
          </Field>
          <Button className={styles.primary} appearance="primary" onClick={() => void preview()}>
            Preview
          </Button>
        </div>
        {err && <div className={x.errText}>{err}</div>}
        {dates && (
          <>
            <div className={x.muted}>{normal}</div>
            <div className={x.chips}>{dates.length === 0 ? "No dates" : dates.map((d) => <span key={d} className={x.dateChip}>{fmtDate(d)}</span>)}</div>
          </>
        )}
      </div>
      <div className={x.section}>
        <h3 className={x.h3}>Repeating tasks</h3>
        <MiniCrud
          ctx={ctx}
          route="automation/auto/recurringtask"
          noun="repeating task"
          fields={fields}
          reloadKey={tasks.length}
          cols={[
            { key: "templateTaskId", label: "Task", render: (r) => <Button size="small" appearance="transparent" onClick={() => ctx.openTask(Number(r.templateTaskId))}>#{str(r.templateTaskId)}</Button> },
            { key: "rrule", label: "Repeats" },
            { key: "nextRunAt", label: "Next", render: (r) => (r.nextRunAt ? fmtDate(str(r.nextRunAt)) : "—") },
            { key: "lastRunAt", label: "Last", render: (r) => (r.lastRunAt ? fmtDate(str(r.lastRunAt)) : "—") },
            { key: "isActive", label: "Status", render: (r) => yesNo(r.isActive) },
          ]}
        />
      </div>
    </>
  );
};

const ReferenceTab: React.FC = () => {
  const cat = useLoad(() => api<AutoCatalog>("GET", "/api/automation/catalog"), []);
  const c = cat.data;
  const group = (title: string, items: string[]) => (
    <div className={x.refGroup} key={title}>
      <h4 className={x.h4}>{title}</h4>
      <div className={x.chips}>
        {items.map((i) => (
          <span key={i} className={x.dateChip}>
            {i}
          </span>
        ))}
      </div>
    </div>
  );
  return (
    <div className={x.section}>
      <Wait loading={cat.loading && !c} error={cat.error} onRetry={cat.reload} />
      {c && (
        <>
          {group("Triggers that fire on an event", c.eventTriggers)}
          {group("Triggers that fire on a schedule", c.scheduledTriggers)}
          {group("Actions a rule can take", c.actions)}
          {group("Fields a rule can set", c.settableFields)}
          {group("Fields a rule condition can use", c.filterFields)}
          {group("Workflow step types", c.workflowSteps)}
        </>
      )}
    </div>
  );
};

export const WorkflowsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("approvals");
  return (
    <>
      <Head icon={<BranchFork20Regular />} title="Workflows" sub="Approvals waiting for you, multi-step workflows, rule tools and repeating tasks." />
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="approvals">Approvals</Tab>
          <Tab value="workflows">Workflows</Tab>
          <Tab value="rules">Rule tools</Tab>
          <Tab value="repeat">Repeating tasks</Tab>
          <Tab value="reference">Reference</Tab>
        </TabList>
      </div>
      <Page>
        {tab === "approvals" && <ApprovalsTab ctx={ctx} />}
        {tab === "workflows" && <DefinitionsTab ctx={ctx} />}
        {tab === "rules" && <RuleToolsTab ctx={ctx} />}
        {tab === "repeat" && <RecurrenceTab ctx={ctx} />}
        {tab === "reference" && <ReferenceTab />}
      </Page>
    </>
  );
};

/* ================================================================== *
 *  Assistant (rule-based helpers + saved suggestions)
 * ================================================================== */
interface Suggestion {
  id: number;
  taskId?: number | null;
  projectId?: number | null;
  suggestionType: string;
  payloadJson: string;
  confidence?: number | null;
  status: string;
  model?: string | null;
  createdAt: string;
}
interface Candidate {
  userId: number;
  name?: string | null;
  openTasks: number;
  openHours: number;
  doneSameProject: number;
  doneRecent: number;
  onLeave: boolean;
  score: number;
}
interface RiskItem {
  key: string;
  title: string;
  probability: number;
  impact: number;
  score: number;
  mitigation?: string | null;
  evidence?: unknown;
}
interface Ranked {
  id: number;
  title: string;
  priority: string;
  dueDate?: string | null;
  score: number;
  reasons: { priority: number; due: number; unblocks: number; quickWin: number };
}
type AiOut =
  | { kind: "breakdown"; subtasks: string[]; source: string; confidence: number; saved: boolean }
  | { kind: "assignee"; candidates: Candidate[]; saved: boolean }
  | { kind: "due"; dueDate: string; confidence: number; basis: { estimateMinutes: number; estimateGiven: boolean; assigneeQueueDays: number; typicalCycleDays: number }; saved: boolean }
  | { kind: "summary"; summary: string; facts: { comments: number; activities: number; subtasks: number; subtasksDone: number; blockers: number | string; lastActivityAt?: string | null } }
  | { kind: "risks"; openTasks: number; risks: RiskItem[]; saved: boolean }
  | { kind: "prioritize"; items: Ranked[] };

const evidenceText = (e: unknown) => (Array.isArray(e) ? e.map(str).join("; ") : e && typeof e === "object" ? JSON.stringify(e) : str(e));
const suggestionText = (type: string, payload: unknown): string => {
  const p = (payload ?? {}) as Record<string, unknown>;
  switch (type) {
    case "SUBTASKS":
      return `Add subtasks: ${Array.isArray(p.titles) ? p.titles.map(str).join(", ") : ""}`;
    case "ASSIGNEE":
      return `Assign to ${str(p.name) || `user ${str(p.userId)}`}`;
    case "DUE_DATE":
      return `Set the due date to ${fmtDate(str(p.date))}`;
    case "RISK":
      return `Risk: ${str(p.title)}. ${str(p.mitigation)}`;
    default:
      return pretty(payload);
  }
};

export const AssistantPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const tasks = useTaskOptions(ctx);
  const [tab, setTab] = useState("ask");
  const [taskId, setTaskId] = useState("");
  const [projectId, setProjectId] = useState(ctx.projectId === null ? "" : String(ctx.projectId));
  const [save, setSave] = useState(true);
  const [mine, setMine] = useState(true);
  const [busy, setBusy] = useState("");
  const [out, setOut] = useState<AiOut | null>(null);
  const [status, setStatus] = useState("PENDING");
  const sugg = useLoad(async () => asList<Suggestion>(await api("GET", "/api/ai/suggestions", undefined, { workspaceId: ctx.workspaceId, status })), [ctx.workspaceId, status]);

  const run = async (name: string, fn: () => Promise<AiOut>) => {
    setBusy(name);
    setOut(null);
    const ok = await act(async () => setOut(await fn()));
    setBusy("");
    if (ok) sugg.reload();
  };
  const tid = Number(taskId);

  return (
    <>
      <Head icon={<Sparkle20Regular />} title="Assistant" sub="Practical helpers that work from your own tasks.">
        <span className={x.muted}>Rule-based (rules-v1), no outside AI model</span>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="ask">Helpers</Tab>
          <Tab value="suggestions">Suggestions</Tab>
        </TabList>
      </div>
      <Page>
        {tab === "ask" && (
          <>
            <div className={x.card}>
              <p className={x.muted}>
                Each helper scores or sorts your real tasks with fixed rules, so you can see why it gave an answer. Anything that would change a task is saved as a suggestion that you accept or reject, never applied on its own.
              </p>
              <h3 className={x.h3}>For one task</h3>
              <TaskSelect tasks={tasks} value={taskId} onChange={setTaskId} />
              <Checkbox label="Save the result as a suggestion to review" checked={save} onChange={(_, d) => setSave(d.checked === true)} />
              <div className={x.rowActions}>
                <Button
                  disabled={!taskId || busy !== ""}
                  onClick={() =>
                    void run("breakdown", async () => {
                      const r = await api<{ source: string; confidence: number; subtasks: string[]; suggestionId?: number | null }>("POST", "/api/ai/breakdown", { taskId: tid, save });
                      return { kind: "breakdown", subtasks: r.subtasks, source: r.source, confidence: r.confidence, saved: !!r.suggestionId };
                    })
                  }
                >
                  Break into subtasks
                </Button>
                <Button
                  disabled={!taskId || busy !== ""}
                  onClick={() =>
                    void run("assignee", async () => {
                      const r = await api<{ candidates: Candidate[]; suggestionId?: number | null }>("POST", "/api/ai/suggest-assignee", { taskId: tid, save });
                      return { kind: "assignee", candidates: r.candidates, saved: !!r.suggestionId };
                    })
                  }
                >
                  Who should do it?
                </Button>
                <Button
                  disabled={!taskId || busy !== ""}
                  onClick={() =>
                    void run("due", async () => {
                      const r = await api<{ dueDate: string; confidence: number; basis: { estimateMinutes: number; estimateGiven: boolean; assigneeQueueDays: number; typicalCycleDays: number }; suggestionId?: number | null }>("POST", "/api/ai/suggest-due-date", { taskId: tid, save });
                      return { kind: "due", dueDate: r.dueDate, confidence: r.confidence, basis: r.basis, saved: !!r.suggestionId };
                    })
                  }
                >
                  Suggest a due date
                </Button>
                <Button
                  disabled={!taskId || busy !== ""}
                  onClick={() =>
                    void run("summary", async () => {
                      const r = await api<{ summary: string; facts: { comments: number; activities: number; subtasks: number; subtasksDone: number; blockers: number | string; lastActivityAt?: string | null } }>("POST", "/api/ai/summarize", { taskId: tid, save: false });
                      return { kind: "summary", summary: r.summary, facts: r.facts };
                    })
                  }
                >
                  Summarise
                </Button>
              </div>
            </div>
            <div className={x.card}>
              <h3 className={x.h3}>For a project or workspace</h3>
              <Field label="Project">
                <Select value={projectId} onChange={(_, d) => setProjectId(d.value)}>
                  <option value="">All projects</option>
                  {ctx.projects.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Checkbox label="Only my tasks (for ranking)" checked={mine} onChange={(_, d) => setMine(d.checked === true)} />
              <div className={x.rowActions}>
                <Button
                  disabled={!projectId || busy !== ""}
                  title={projectId ? "" : "Choose a project"}
                  onClick={() =>
                    void run("risks", async () => {
                      const r = await api<{ openTasks: number; risks: RiskItem[]; suggestionIds?: number[] }>("POST", "/api/ai/risks", { projectId: Number(projectId), save });
                      return { kind: "risks", openTasks: r.openTasks, risks: r.risks, saved: (r.suggestionIds ?? []).length > 0 };
                    })
                  }
                >
                  Find project risks
                </Button>
                <Button
                  disabled={busy !== ""}
                  onClick={() =>
                    void run("prioritize", async () => {
                      const r = await api<{ items: Ranked[] }>("POST", "/api/ai/prioritize", { workspaceId: ctx.workspaceId, projectId: projectId ? Number(projectId) : null, mineOnly: mine });
                      return { kind: "prioritize", items: r.items };
                    })
                  }
                >
                  Rank what to do first
                </Button>
              </div>
            </div>
            {busy && <Spinner size="small" label="Working…" />}
            {out && <AiResult out={out} ctx={ctx} />}
          </>
        )}
        {tab === "suggestions" && (
          <div className={x.section}>
            <TabList size="small" selectedValue={status} onTabSelect={(_, d) => setStatus(String(d.value))}>
              <Tab value="PENDING">To review</Tab>
              <Tab value="ACCEPTED">Accepted</Tab>
              <Tab value="REJECTED">Rejected</Tab>
            </TabList>
            <Wait loading={sugg.loading && !sugg.data} error={sugg.error} empty={!sugg.loading && (sugg.data ?? []).length === 0 && "No suggestions here"} onRetry={sugg.reload} />
            {(sugg.data ?? []).map((s) => (
              <div key={s.id} className={x.approval}>
                <div className={x.grow}>
                  <div className={x.strong}>
                    {nice(s.suggestionType)} {s.confidence !== null && s.confidence !== undefined && <span className={x.muted}>· {Math.round(Number(s.confidence) * 100)}% sure</span>}
                  </div>
                  <div>{suggestionText(s.suggestionType, parseJson(s.payloadJson))}</div>
                  <div className={x.muted}>{fmtStamp(s.createdAt)}</div>
                </div>
                {s.taskId ? (
                  <Button size="small" onClick={() => ctx.openTask(Number(s.taskId))}>
                    Open task
                  </Button>
                ) : null}
                {s.status === "PENDING" && (
                  <>
                    <Button
                      size="small"
                      appearance="primary"
                      className={styles.primary}
                      onClick={async () => {
                        let applied = 0;
                        const ok = await act(async () => {
                          applied = (await api<{ applied: number }>("POST", `/api/ai/suggestions/${s.id}/accept`)).applied;
                        });
                        if (ok) {
                          ctx.notify("success", applied > 0 ? `Applied (${applied} change${applied === 1 ? "" : "s"}).` : "Accepted.");
                          sugg.reload();
                        }
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      size="small"
                      onClick={async () => {
                        if (await act(() => api("POST", `/api/ai/suggestions/${s.id}/reject`), "Rejected.")) sugg.reload();
                      }}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Page>
    </>
  );
};

const AiResult: React.FC<{ out: AiOut; ctx: PageCtx }> = ({ out, ctx }) => {
  const saved = "saved" in out && out.saved ? <div className={x.muted}>Saved under Suggestions so you can accept or reject it.</div> : null;
  switch (out.kind) {
    case "breakdown":
      return (
        <div className={x.card}>
          <h3 className={x.h3}>Suggested subtasks</h3>
          <div className={x.muted}>
            Based on {out.source.toLowerCase().replace(/_/g, " ")} · {Math.round(out.confidence * 100)}% sure
          </div>
          {out.subtasks.length === 0 ? <div className={x.muted}>Nothing to suggest for this task.</div> : <ol className={x.history}>{out.subtasks.map((s, i) => <li key={i}>{s}</li>)}</ol>}
          {saved}
        </div>
      );
    case "assignee":
      return (
        <div className={x.card}>
          <h3 className={x.h3}>Who could take it</h3>
          <table className={x.tbl}>
            <thead>
              <tr>
                <th>Person</th>
                <th>Open tasks</th>
                <th>Open hours</th>
                <th>Done in project</th>
                <th>Done lately</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {out.candidates.map((c) => (
                <tr key={c.userId}>
                  <td className={x.strong}>
                    {c.name || userName(ctx, c.userId)} {c.onLeave && <Chip text="On leave" />}
                  </td>
                  <td>{c.openTasks}</td>
                  <td>{c.openHours}</td>
                  <td>{c.doneSameProject}</td>
                  <td>{c.doneRecent}</td>
                  <td>{c.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {out.candidates.length === 0 && <div className={x.muted}>No one else in the workspace to suggest.</div>}
          {saved}
        </div>
      );
    case "due":
      return (
        <div className={x.card}>
          <h3 className={x.h3}>Suggested due date: {fmtDate(out.dueDate)}</h3>
          <div className={x.muted}>
            {Math.round(out.confidence * 100)}% sure · estimate {out.basis.estimateMinutes} min{out.basis.estimateGiven ? "" : " (assumed)"} · assignee queue {out.basis.assigneeQueueDays} days · typical cycle {out.basis.typicalCycleDays} days
          </div>
          {saved}
        </div>
      );
    case "summary":
      return (
        <div className={x.card}>
          <h3 className={x.h3}>Summary</h3>
          <p>{out.summary}</p>
          <div className={x.tiles}>
            <Tile n={out.facts.comments} label="Comments" />
            <Tile n={`${out.facts.subtasksDone}/${out.facts.subtasks}`} label="Subtasks done" />
            <Tile n={str(out.facts.blockers)} label="Blockers" />
            <Tile n={out.facts.lastActivityAt ? fmtDate(out.facts.lastActivityAt) : "—"} label="Last activity" />
          </div>
        </div>
      );
    case "risks":
      return (
        <div className={x.card}>
          <h3 className={x.h3}>Project risks ({out.openTasks} open tasks checked)</h3>
          {out.risks.length === 0 && <div className={x.muted}>No risks found with the current rules.</div>}
          {out.risks.map((r) => (
            <div key={r.key} className={x.answer}>
              <div className={x.answerQ}>
                {r.title} <span className={x.muted}>· chance {r.probability} × impact {r.impact} = {r.score}</span>
              </div>
              {r.mitigation && <div>{r.mitigation}</div>}
              {r.evidence !== undefined && r.evidence !== null && <div className={x.muted}>{evidenceText(r.evidence)}</div>}
            </div>
          ))}
          {saved}
        </div>
      );
    case "prioritize":
      return (
        <div className={x.card}>
          <h3 className={x.h3}>Do these first</h3>
          {out.items.length === 0 && <div className={x.muted}>No open tasks to rank.</div>}
          <table className={x.tbl}>
            <tbody>
              {out.items.map((t, i) => (
                <tr key={t.id} className={x.click} onClick={() => ctx.openTask(t.id)}>
                  <td>{i + 1}</td>
                  <td className={x.strong}>{t.title}</td>
                  <td>{t.dueDate ? fmtDate(t.dueDate) : "No date"}</td>
                  <td className={x.muted}>
                    priority {t.reasons.priority} · due {t.reasons.due} · unblocks {t.reasons.unblocks} · quick win {t.reasons.quickWin}
                  </td>
                  <td className={x.strong}>{t.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
};

/* ================================================================== *
 *  Industry packs
 * ================================================================== */
interface PackCat {
  code: string;
  name: string;
  description: string;
  entities: string[];
}
interface PackState {
  code: string;
  enabled: boolean;
  config?: string | null;
}
interface WoCol {
  status: string;
  orders: { id: number; assetName?: string | null; location?: string | null; problemDescription: string; priority: string; status: string; assigneeUserId?: number | null; scheduledFor?: string | null }[];
}
interface HealthSummary {
  byStatus: { status: string; customers: number; avgScore?: number | null }[];
  needsAttention: { id: number; customerName: string; healthScore?: number | null; status: string; lastReviewAt?: string | null; ownerUserId?: number | null }[];
}
interface Burn {
  engagement: { clientName: string; billingModel: string; budget?: number | null; currencyCode?: string | null };
  hours: number;
  billableHours: number;
  amounts: { currency: string; amount: number }[];
  budgetUsedPercent?: number | null;
}
const WO_STATUSES = ["NEW", "SCHEDULED", "IN_PROGRESS", "ON_HOLD", "COMPLETED", "CANCELLED"];
const PRIOS = opts(["LOW", "MEDIUM", "HIGH", "URGENT"]);

const WorkOrders: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const board = useLoad(async () => asList<WoCol>(await api("GET", "/api/verticals/work-orders/board", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<number | null>(null);
  const [state, setState] = useState<FormState>({});
  const [busy, setBusy] = useState(false);
  const fields: FieldDef[] = [
    { key: "assetName", label: "Asset", kind: "text" },
    { key: "location", label: "Location", kind: "text" },
    { key: "problemDescription", label: "What is wrong", kind: "textarea", required: true },
    { key: "priority", label: "Priority", kind: "select", required: true, options: PRIOS, initial: "MEDIUM" },
    { key: "status", label: "Status", kind: "select", required: true, options: opts(WO_STATUSES), initial: "NEW" },
    { key: "assigneeUserId", label: "Assigned to", kind: "user" },
    { key: "scheduledFor", label: "Scheduled for", kind: "datetime", hint: "Required when the status is Scheduled." },
  ];
  const cols = board.data ?? [];
  const startEdit = async (id: number) => {
    try {
      const row = await crud.get<Row>("verticals/vert/workorder", id);
      setEdit(id);
      setState(stateFromRow(fields, row));
      setOpen(true);
    } catch (e) {
      ctx.notify("error", msgOf(e));
    }
  };
  const submit = async () => {
    const r = bodyFromState(fields, state, edit !== null);
    if ("error" in r) return ctx.notify("error", r.error);
    setBusy(true);
    const ok = await act(() => (edit ? crud.update("verticals/vert/workorder", edit, r.body) : crud.create("verticals/vert/workorder", { workspaceId: ctx.workspaceId, ...r.body })), edit ? "Saved." : "Work order added.");
    setBusy(false);
    if (ok) {
      setOpen(false);
      board.reload();
    }
  };
  return (
    <div className={x.section}>
      <div className={x.sectionBar}>
        <Button
          className={styles.primary}
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => {
            setEdit(null);
            setState(emptyState(fields));
            setOpen(true);
          }}
        >
          New work order
        </Button>
      </div>
      <Wait loading={board.loading && !board.data} error={board.error} onRetry={board.reload} />
      <div className={x.kanban}>
        {cols.map((c) => (
          <section key={c.status} className={x.kcol} aria-label={nice(c.status)}>
            <header className={x.kHead}>
              {nice(c.status)} <span className={x.count}>{c.orders.length}</span>
            </header>
            {c.orders.map((o) => (
              <div key={o.id} className={x.kcard}>
                <button type="button" className={x.kTitle} onClick={() => void startEdit(o.id)}>
                  {o.assetName || `Work order ${o.id}`}
                </button>
                <div className={x.muted}>{o.location}</div>
                <div>{o.problemDescription}</div>
                <div className={x.chips}>
                  <Chip text={o.priority} />
                  {o.assigneeUserId ? <span className={x.muted}>{userName(ctx, o.assigneeUserId)}</span> : null}
                </div>
                {o.scheduledFor && <div className={x.muted}>Scheduled {fmtStamp(o.scheduledFor)}</div>}
                <Select
                  size="small"
                  aria-label="Move work order"
                  value={o.status}
                  onChange={async (_, d) => {
                    const body: Record<string, unknown> = { status: d.value };
                    if (d.value === "SCHEDULED" && !o.scheduledFor) {
                      ctx.notify("info", "Open the work order and pick a date to schedule it.");
                      return;
                    }
                    if (await act(() => crud.update("verticals/vert/workorder", o.id, body))) board.reload();
                  }}
                >
                  {WO_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {nice(s)}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
            {c.orders.length === 0 && <div className={x.muted}>Nothing here</div>}
          </section>
        ))}
      </div>
      <EntityDialog open={open} title={edit ? "Edit work order" : "New work order"} action={edit ? "Save" : "Add"} fields={fields} state={state} setState={setState} editing={edit !== null} busy={busy} ctx={ctx} onClose={() => setOpen(false)} onSubmit={() => void submit()} />
    </div>
  );
};

const Rfis: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [key, setKey] = useState(0);
  const late = useLoad(async () => asList<{ id: number; subject: string; dueDate: string; assigneeUserId?: number | null; daysLate: number }>(await api("GET", "/api/verticals/rfis/overdue", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId, key]);
  const fields: FieldDef[] = [
    { key: "subject", label: "Subject", kind: "text", required: true },
    { key: "projectId", label: "Project", kind: "project" },
    { key: "question", label: "Question", kind: "textarea" },
    { key: "answer", label: "Answer", kind: "textarea", hint: "Answering moves it to Answered." },
    { key: "status", label: "Status", kind: "select", options: opts(["OPEN", "ANSWERED", "CLOSED"]), initial: "OPEN" },
    { key: "dueDate", label: "Due", kind: "date" },
    { key: "assigneeUserId", label: "Assigned to", kind: "user" },
  ];
  return (
    <>
      {(late.data ?? []).length > 0 && (
        <div className={x.alert}>
          <strong>{(late.data ?? []).length} overdue</strong>
          {(late.data ?? []).map((r) => (
            <div key={r.id}>
              {r.subject} · due {fmtDate(r.dueDate)} · {r.daysLate} day{r.daysLate === 1 ? "" : "s"} late · {userName(ctx, r.assigneeUserId)}
            </div>
          ))}
        </div>
      )}
      <MiniCrud
        ctx={ctx}
        route="verticals/vert/rfi"
        noun="RFI"
        fields={fields}
        onChanged={() => setKey((k) => k + 1)}
        cols={[
          { key: "subject", label: "Subject" },
          { key: "status", label: "Status", render: (r) => <Chip text={str(r.status)} /> },
          { key: "dueDate", label: "Due", render: (r) => (r.dueDate ? fmtDate(str(r.dueDate)) : "—") },
          { key: "assigneeUserId", label: "Assigned", render: (r) => userName(ctx, r.assigneeUserId) },
        ]}
      />
    </>
  );
};

const CustomerHealthTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [key, setKey] = useState(0);
  const sum = useLoad(() => api<HealthSummary>("GET", "/api/verticals/customer-health/summary", undefined, { workspaceId: ctx.workspaceId }), [ctx.workspaceId, key]);
  const fields: FieldDef[] = [
    { key: "customerName", label: "Customer", kind: "text", required: true },
    { key: "healthScore", label: "Health score (0–100)", kind: "number" },
    { key: "status", label: "Status", kind: "select", options: opts(["GREEN", "YELLOW", "RED"]), initial: "GREEN" },
    { key: "lastReviewAt", label: "Last review", kind: "datetime" },
    { key: "ownerUserId", label: "Owner", kind: "user" },
  ];
  return (
    <>
      <Wait loading={sum.loading && !sum.data} error={sum.error} onRetry={sum.reload} />
      {sum.data && (
        <>
          <div className={x.tiles}>
            {sum.data.byStatus.map((s) => (
              <Tile key={s.status} n={`${s.customers}${s.avgScore !== null && s.avgScore !== undefined ? ` · avg ${s.avgScore}` : ""}`} label={nice(s.status)} />
            ))}
          </div>
          {sum.data.needsAttention.length > 0 && (
            <div className={x.alert}>
              <strong>Needs attention</strong>
              {sum.data.needsAttention.map((c) => (
                <div key={c.id}>
                  {c.customerName} · {nice(c.status)} · score {c.healthScore ?? "—"} · {c.lastReviewAt ? `reviewed ${fmtDate(c.lastReviewAt)}` : "never reviewed"} · {userName(ctx, c.ownerUserId)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <MiniCrud
        ctx={ctx}
        route="verticals/vert/customerhealth"
        noun="customer"
        fields={fields}
        onChanged={() => setKey((k) => k + 1)}
        cols={[
          { key: "customerName", label: "Customer" },
          { key: "healthScore", label: "Score" },
          { key: "status", label: "Status", render: (r) => <Chip text={str(r.status) === "GREEN" ? "On track" : str(r.status) === "RED" ? "Critical" : "At risk"} /> },
          { key: "lastReviewAt", label: "Reviewed", render: (r) => (r.lastReviewAt ? fmtDate(str(r.lastReviewAt)) : "Never") },
          { key: "ownerUserId", label: "Owner", render: (r) => userName(ctx, r.ownerUserId) },
        ]}
      />
    </>
  );
};

const BurnDialog: React.FC<{ id: number | null; onClose: () => void }> = ({ id, onClose }) => {
  const b = useLoad(() => (id === null ? Promise.resolve(null) : api<Burn>("GET", `/api/verticals/engagements/${id}/burn`)), [id]);
  const d = b.data;
  return (
    <Dialog open={id !== null} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Budget burn{d ? ` · ${d.engagement.clientName}` : ""}
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <Wait loading={b.loading && !d} error={b.error} onRetry={b.reload} />
            {d && (
              <>
                <div className={x.tiles}>
                  <Tile n={`${d.hours} h`} label="Hours logged" />
                  <Tile n={`${d.billableHours} h`} label="Billable" />
                  {d.amounts.map((a) => (
                    <Tile key={a.currency} n={`${a.amount.toLocaleString()} ${a.currency}`} label="Billed" />
                  ))}
                </div>
                {d.budgetUsedPercent !== null && d.budgetUsedPercent !== undefined ? (
                  <>
                    <div className={x.muted}>
                      {d.budgetUsedPercent}% of the {d.engagement.budget?.toLocaleString()} {d.engagement.currencyCode} budget used
                    </div>
                    <ProgressBar value={Math.min(1, d.budgetUsedPercent / 100)} color={d.budgetUsedPercent > 100 ? "error" : d.budgetUsedPercent > 85 ? "warning" : "brand"} />
                  </>
                ) : (
                  <div className={x.muted}>No budget is set, or it is in a currency without matching time entries.</div>
                )}
              </>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

const PACK_TABS: Record<string, { label: string; render: (ctx: PageCtx) => React.ReactNode }[]> = {
  CONSTRUCTION: [
    { label: "RFIs", render: (ctx) => <Rfis ctx={ctx} /> },
    {
      label: "Punch list",
      render: (ctx) => (
        <MiniCrud
          ctx={ctx}
          route="verticals/vert/punchitem"
          noun="punch item"
          fields={[
            { key: "description", label: "What needs fixing", kind: "textarea", required: true },
            { key: "location", label: "Location", kind: "text" },
            { key: "projectId", label: "Project", kind: "project" },
            { key: "status", label: "Status", kind: "select", options: opts(["OPEN", "IN_PROGRESS", "DONE"]), initial: "OPEN" },
            { key: "assigneeUserId", label: "Assigned to", kind: "user" },
            { key: "dueDate", label: "Due", kind: "date" },
          ]}
          cols={[
            { key: "description", label: "Item" },
            { key: "location", label: "Where" },
            { key: "status", label: "Status", render: (r) => <Chip text={str(r.status)} /> },
            { key: "dueDate", label: "Due", render: (r) => (r.dueDate ? fmtDate(str(r.dueDate)) : "—") },
            { key: "assigneeUserId", label: "Assigned", render: (r) => userName(ctx, r.assigneeUserId) },
          ]}
        />
      ),
    },
  ],
  FACILITIES: [{ label: "Work orders", render: (ctx) => <WorkOrders ctx={ctx} /> }],
  MARKETING: [
    {
      label: "Campaign assets",
      render: (ctx) => (
        <MiniCrud
          ctx={ctx}
          route="verticals/vert/campaignasset"
          noun="asset"
          fields={[
            { key: "name", label: "Name", kind: "text", required: true },
            { key: "projectId", label: "Project", kind: "project" },
            { key: "channel", label: "Channel", kind: "text" },
            { key: "publishDate", label: "Publish on", kind: "date" },
            { key: "approvalStatus", label: "Approval", kind: "select", options: opts(["DRAFT", "IN_REVIEW", "APPROVED", "REJECTED"]), initial: "DRAFT" },
            { key: "approvedBy", label: "Approved by", kind: "user" },
          ]}
          cols={[
            { key: "name", label: "Asset" },
            { key: "channel", label: "Channel" },
            { key: "publishDate", label: "Publish", render: (r) => (r.publishDate ? fmtDate(str(r.publishDate)) : "—") },
            { key: "approvalStatus", label: "Approval", render: (r) => <Chip text={str(r.approvalStatus)} /> },
          ]}
        />
      ),
    },
  ],
  HR_ONBOARDING: [
    {
      label: "Onboarding plans",
      render: (ctx) => (
        <MiniCrud
          ctx={ctx}
          route="verticals/vert/onboardingplan"
          noun="plan"
          fields={[
            { key: "employeeName", label: "New employee", kind: "text", required: true },
            { key: "employeeUserId", label: "Their account", kind: "user" },
            { key: "startDate", label: "Starts", kind: "date" },
            { key: "projectId", label: "Project", kind: "project" },
            { key: "status", label: "Status", kind: "select", options: opts(["PLANNED", "IN_PROGRESS", "COMPLETED"]), initial: "PLANNED" },
          ]}
          cols={[
            { key: "employeeName", label: "Employee" },
            { key: "startDate", label: "Starts", render: (r) => (r.startDate ? fmtDate(str(r.startDate)) : "—") },
            { key: "status", label: "Status", render: (r) => <Chip text={str(r.status)} /> },
          ]}
        />
      ),
    },
  ],
  PROFESSIONAL_SERVICES: [{ label: "Engagements", render: (ctx) => <Engagements ctx={ctx} /> }],
  CUSTOMER_SUCCESS: [{ label: "Customer health", render: (ctx) => <CustomerHealthTab ctx={ctx} /> }],
};

const Engagements: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [burn, setBurn] = useState<number | null>(null);
  return (
    <>
      <MiniCrud
        ctx={ctx}
        route="verticals/vert/clientengagement"
        noun="engagement"
        fields={[
          { key: "clientName", label: "Client", kind: "text", required: true },
          { key: "projectId", label: "Project", kind: "project" },
          { key: "billingModel", label: "Billing", kind: "select", options: opts(["HOURLY", "FIXED", "RETAINER"]), initial: "HOURLY" },
          { key: "budget", label: "Budget", kind: "number" },
          { key: "currencyCode", label: "Currency (3 letters)", kind: "text" },
          { key: "utilizationTarget", label: "Utilisation target (%)", kind: "number" },
        ]}
        cols={[
          { key: "clientName", label: "Client" },
          { key: "billingModel", label: "Billing", render: (r) => nice(str(r.billingModel)) },
          { key: "budget", label: "Budget", render: (r) => (r.budget ? `${Number(r.budget).toLocaleString()} ${str(r.currencyCode)}` : "—") },
          { key: "utilizationTarget", label: "Target", render: (r) => (r.utilizationTarget ? `${str(r.utilizationTarget)}%` : "—") },
        ]}
        rowActions={(r) => (
          <Button size="small" onClick={() => setBurn(r.id)}>
            Budget burn
          </Button>
        )}
      />
      <BurnDialog id={burn} onClose={() => setBurn(null)} />
    </>
  );
};

export const VerticalsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const isAdmin = ctx.me.workspaces.find((w:any) => w.id === ctx.workspaceId)?.role === "ADMIN";
  const cat = useLoad(async () => asList<PackCat>(await api("GET", "/api/verticals/catalog")), []);
  const packs = useLoad(async () => asList<PackState>(await api("GET", "/api/verticals/packs", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId]);
  const [tab, setTab] = useState("packs");
  const [pack, setPack] = useState("");
  const [cfgFor, setCfgFor] = useState<PackCat | null>(null);
  const [cfg, setCfg] = useState("");
  const enabled = (code: string) => (packs.data ?? []).find((p) => p.code === code)?.enabled === true;
  const on = (cat.data ?? []).filter((c) => enabled(c.code));

  useEffect(() => {
    if (tab !== "packs" && !enabled(tab)) setTab("packs");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packs.data]);

  const toggle = async (c: PackCat, to: boolean, config?: string) => {
    setPack(c.code);
    const ok = await act(
      () => api("POST", `/api/verticals/packs/${c.code}/${to ? "enable" : "disable"}`, { workspaceId: ctx.workspaceId, configJson: config?.trim() ? config.trim() : null }),
      to ? `${c.name} is on.` : `${c.name} is off.`,
    );
    setPack("");
    if (ok) packs.reload();
    return ok;
  };

  return (
    <>
      <Head icon={<Building20Regular />} title="Industry packs" sub="Switch on extra records and boards for the way your team works.">
        {!isAdmin && <span className={x.muted}>Only workspace admins can switch packs on or off</span>}
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="packs">Packs</Tab>
          {on.map((c) => (
            <Tab key={c.code} value={c.code}>
              {c.name}
            </Tab>
          ))}
        </TabList>
      </div>
      <Page>
        {tab === "packs" && (
          <>
            <Wait loading={(cat.loading && !cat.data) || (packs.loading && !packs.data)} error={cat.error || packs.error} onRetry={() => (cat.reload(), packs.reload())} />
            <div className={x.cards}>
              {(cat.data ?? []).map((c) => {
                const p = (packs.data ?? []).find((q) => q.code === c.code);
                return (
                  <div key={c.code} className={x.packCard}>
                    <div className={x.rowBetween}>
                      <span className={x.strong}>{c.name}</span>
                      <Switch checked={enabled(c.code)} disabled={!isAdmin || pack === c.code} aria-label={`${c.name} is ${enabled(c.code) ? "on" : "off"}`} onChange={(_, d) => void toggle(c, d.checked)} />
                    </div>
                    <div className={x.muted}>{c.description}</div>
                    <div className={x.chips}>
                      {c.entities.map((e) => (
                        <span key={e} className={x.dateChip}>
                          {e}
                        </span>
                      ))}
                    </div>
                    {isAdmin && (
                      <Button
                        size="small"
                        onClick={() => {
                          setCfgFor(c);
                          setCfg(p?.config ? pretty(p.config) : "");
                        }}
                      >
                        Settings (JSON)
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {tab !== "packs" && <PackBody code={tab} ctx={ctx} />}
      </Page>
      <FormDialog
        open={cfgFor !== null}
        title={cfgFor ? `${cfgFor.name} settings` : "Settings"}
        action="Save settings"
        onClose={() => setCfgFor(null)}
        onSubmit={async () => {
          if (!cfgFor) return;
          if (cfg.trim()) {
            try {
              JSON.parse(cfg);
            } catch {
              return ctx.notify("error", "The settings are not valid JSON.");
            }
          }
          if (await toggle(cfgFor, enabled(cfgFor.code), cfg)) setCfgFor(null);
        }}
      >
        <Field label="Settings" hint="Saved with the pack; it stays in its current on/off state.">
          <Textarea className={x.mono} value={cfg} rows={8} resize="vertical" onChange={(_, d) => setCfg(d.value)} />
        </Field>
      </FormDialog>
    </>
  );
};

const PackBody: React.FC<{ code: string; ctx: PageCtx }> = ({ code, ctx }) => {
  const parts = PACK_TABS[code] ?? [];
  const [i, setI] = useState(0);
  const cur = parts[Math.min(i, Math.max(0, parts.length - 1))];
  return (
    <>
      {parts.length > 1 && (
        <TabList size="small" selectedValue={String(i)} onTabSelect={(_, d) => setI(Number(d.value))}>
          {parts.map((p, n) => (
            <Tab key={p.label} value={String(n)}>
              {p.label}
            </Tab>
          ))}
        </TabList>
      )}
      {cur ? cur.render(ctx) : <div className={styles.emptyRow}>This pack has no screens yet.</div>}
    </>
  );
};